import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Brand } from "@/components/Brand";
import { CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const fired = useRef("");
  const [state, setState] = useState({ loading: true, valid: false, error: "" });
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  useEffect(() => {
    if (fired.current === token) return;
    fired.current = token;
    axios.get(`${API}/public/reset-password/${encodeURIComponent(token)}`)
      .then(() => setState({ loading: false, valid: true, error: "" }))
      .catch(e => setState({ loading: false, valid: false, error: e?.response?.data?.detail || "This reset link is not valid." }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErrMsg("");
    if (password.length < 12) { setErrMsg("Password must be at least 12 characters."); return; }
    if (password !== confirm) { setErrMsg("Passwords do not match."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/public/reset-password/${encodeURIComponent(token)}`, { password });
      localStorage.removeItem("sahoi_token");
      toast.success("Password updated — please sign in.");
      navigate("/login", { replace: true });
    } catch (e) {
      setErrMsg(e?.response?.data?.detail || "Could not reset the password. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F8FAF9] text-slate-900" data-testid="reset-password-page">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-4 py-3"><Link to="/"><Brand variant="dark" size={18} /></Link></div>
      </header>
      <main className="max-w-md mx-auto px-4 py-12">
        {state.loading && <div className="text-sm text-slate-500" data-testid="reset-loading">Checking link…</div>}
        {!state.loading && !state.valid && (
          <div className="p-5 rounded-lg border border-amber-300 bg-amber-50" data-testid="reset-error">
            <AlertTriangle className="w-5 h-5 text-amber-700 mb-2" />
            <div className="text-sm font-semibold text-amber-900">This reset link is not valid</div>
            <div className="text-xs text-amber-800 mt-1">{state.error}</div>
            <Link to="/forgot-password" className="mt-3 inline-flex items-center text-sm font-semibold underline text-amber-900" data-testid="reset-request-new">Request a new link</Link>
          </div>
        )}
        {state.valid && (
          <form onSubmit={submit} className="p-6 rounded-lg border border-slate-200 bg-white space-y-4" data-testid="reset-form">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Account recovery</div>
              <h1 className="text-2xl font-bold mt-1 tracking-[-0.01em]">Choose a new password</h1>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">12 characters minimum. This link works one time, and every other device will be signed out.</p>
            </div>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">New password</span>
              <input type="password" required minLength={12} value={password} onChange={e => setPassword(e.target.value)} autoComplete="new-password"
                     className="w-full mt-1 border border-slate-300 rounded-md px-3 py-3 text-base min-h-[48px] bg-white" data-testid="reset-password" />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Confirm password</span>
              <input type="password" required minLength={12} value={confirm} onChange={e => setConfirm(e.target.value)} autoComplete="new-password"
                     className="w-full mt-1 border border-slate-300 rounded-md px-3 py-3 text-base min-h-[48px] bg-white" data-testid="reset-confirm" />
            </label>
            {errMsg && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="reset-err">{errMsg}</div>}
            <button type="submit" disabled={busy} className="w-full bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white font-semibold rounded-md py-3 min-h-[48px]" data-testid="reset-submit">
              {busy ? "Saving…" : "Save new password"}
            </button>
            <div className="text-[11px] text-slate-500 flex gap-1.5 items-start pt-1">
              <CheckCircle2 className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />
              <span>Your password is only stored as a hash. Throughline never sees it.</span>
            </div>
          </form>
        )}
      </main>
    </div>
  );
}
